import { FormikProvider, FormikValues, useFormik } from 'formik'
import React, { useEffect } from 'react'
import { ExitUnsavedFormHandler } from './ExitUnsavedFormHandler'

interface Props<T extends FormikValues> {
  formKey: string
  initialValues: T
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationSchema?: any
  children: React.ReactElement
  validateOnMount?: boolean
  onSubmit: (values: T, initialValues?: T) => void
  enableReinitialize?: boolean
}

/**
 * Extends formik by including an exit handler for cases where users leave a form with unsaved changes.
 */
const FormikWithExitHandler = <T extends FormikValues>({
  formKey,
  initialValues,
  validationSchema,
  onSubmit,
  children,
  enableReinitialize = true,
  validateOnMount = true,
  ...props
}: Props<T>): React.ReactElement => {
  const formik = useFormik({
    enableReinitialize,
    initialValues,
    validationSchema,
    validateOnMount,
    onSubmit: (values, { resetForm }) => {
      onSubmit(values, initialValues)
      resetForm({ values }) // set dirty to false
    },
    ...props,
  })

  useEffect(() => {
    if (validateOnMount) {
      formik.validateForm().then(formErrors => {
        Object.keys(formErrors).forEach(key => {
          if (!formik.touched[key]) {
            formik.setFieldTouched(key, true, true)
          }
        })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateOnMount, formik.touched])

  return (
    <FormikProvider value={formik}>
      <ExitUnsavedFormHandler formKey={formKey} onSubmit={onSubmit}>
        {children}
      </ExitUnsavedFormHandler>
    </FormikProvider>
  )
}

export default FormikWithExitHandler
